import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gql } from 'graphql-tag';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
/* Nav chips are not translated; display raw values */
import { useClient, useMutation, useQuery } from 'urql';
import { AppContext } from '~/App';
import { Switch } from '~/components/form/switch/Switch';
import { TextArea as AutoTextArea } from '~/components/form/textarea/TextArea';
import { Confirm } from '~/components/ui/Confirm';
import { Label } from '~/components/ui/Label';
import { Tooltip } from '~/components/ui/Tooltip';
import type { ContactInput, ContactType, Status } from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { NavTypesPicker } from '../shared/NavTypesPicker';
import { serializeByKey } from '../shared/serializedPersist';
import {
  arraysEqual,
  deriveTypesFromText,
  uniqueSorted,
} from '../shared/typesConfigUtils';
import { saveMultiRenamesIfAny } from '../shared/typesEditUtils';
import { networkOnly, networkOnlyWithSignal } from '../shared/urqlHelpers';
import { useBackgroundTypeUpdater } from '../shared/useBackgroundTypeUpdater';
import { useMultiTypeReassignState } from '../shared/useMultiTypeReassignState';
import { useProgressToast } from '../shared/useProgressToast';
import { useTypesEditingFlow } from '../shared/useTypesEditingFlow';
// import { useTypesSafeguards } from '../shared/useTypesSafeguards';
import {
  MAX_INFERRED_NAV_ITEMS,
  OTHER_TYPE,
  writeShowAllToStorage,
} from './shared/constants';

// IMPORTANT:
// "Contact Types" in this UI refers to the tenant-configured relationship categories
// (ultimately persisted in server config and reflected via Contact.relationship arrays).
// This component lets users:
//  - Edit the preset list and order of relationship categories (types)
//  - Choose which categories appear as tabs in the Contacts sub-navigation and their order
//  - Toggle whether an "All" tab is shown
// Contact documents themselves are not retyped here except during rename/delete safeguards
// where existing contacts may be updated to keep data consistent.

// use shared deriveTypesFromText and isLikelyPluralization from utils
// GraphQL
const CONTACT_TYPE_CONFIG_Q = gql`
  query ContactTypeConfig_Relationships {
    contactTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`;
const CONTACTS_BY_REL_Q = gql`
  query ContactsByRelationship($relationship: [String!]) {
    contacts(relationship: $relationship) {
      id
      name
      status
      type
      relationship
    }
  }
`;
// Lightweight live-check for in-use relationships using server-provided list
const CONTACTS_RELLESS_Q = gql`
  query ContactsRelless_LiveCheck {
    hasUntypedContacts
    contactTypesInUse
  }
`;
// Matches the main list query so issuing this will refresh the list cache
const CONTACTS_REFRESH_Q = gql`
  query Contacts($status: [Status!], $relationship: [String!]) {
    contacts(status: $status, relationship: $relationship) {
      id
      status
      type
      name
      email
      phone
      relationship
      image
      isCurrentUser
      isTeamMember
    }
  }
`;
const CONTACT_TYPE_MAPPINGS_Q = gql`
  query ContactTypeMappingsForList {
    contactTypeMappings {
      label
      relationships
    }
  }
`;
const UPDATE_CONTACT_M = gql`
  mutation UpdateContact($id: ID!, $input: ContactInput!) {
    updateContact(id: $id, input: $input) {
      id
      name
      status
      type
      relationship
      __typename
    }
  }
`;
const CONTACT_TYPES_PRESET_Q = gql`
  query ContactTypesPreset($preset: Boolean) {
    contactTypes(preset: $preset)
  }
`;
const CONTACT_TYPES_IN_USE_Q = gql`
  query ContactTypesInUse {
    contactTypesInUse
  }
`;
const ALL_CONTACTS_FOR_TYPES_Q = gql`
  query AllContactsForTypes {
    contacts(status: [Active, Inactive]) {
      id
      relationship
    }
  }
`;
const UPDATE_CONTACT_TYPE_CONFIG_M = gql`
  mutation UpdateContactTypeConfig_Relationships(
    $input: UpdatePlaceTypeConfigInput!
  ) {
    updateContactTypeConfig(input: $input) {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`;

export function ManageContactTypes({
  onClose,
  onSaveReady,
  useExternalActions = false,
  onDirtyChange,
}: {
  onClose: () => void;
  onSaveReady?: (save: () => void) => void;
  useExternalActions?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  // no translations used for chips
  const { tenant } = useContext(AppContext);
  const progress = useProgressToast();
  const tenantKey = tenant?.id || 'default';
  const localStorage = useLocalStorage();
  const client = useClient();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const scrollPos = useRef<number>(0);
  const [{ data: cfgData }] = useQuery({
    query: CONTACT_TYPE_CONFIG_Q,
    requestPolicy: 'cache-and-network',
  });
  const [{ data: presetData }] = useQuery({
    query: CONTACT_TYPES_PRESET_Q,
    variables: { preset: true },
    requestPolicy: 'cache-and-network',
  });
  const [{ data: inUseData }] = useQuery({
    query: CONTACT_TYPES_IN_USE_Q,
    requestPolicy: 'cache-and-network',
  });
  // Fetch all contacts to derive types in use directly from data
  const [{ data: allContactsData }] = useQuery({
    query: ALL_CONTACTS_FOR_TYPES_Q,
    requestPolicy: 'cache-and-network',
  });
  const [errorMessage, setError] = useState<string>('');
  const [infoMessage, setInfo] = useState<string>('');

  // Derive types in use from actual contact data (similar to how assets work)
  const typesInUseFromContacts = useMemo(() => {
    const contacts = allContactsData?.contacts ?? [];

    const allRelationships: string[] = [];
    contacts.forEach((c: { relationship?: string[] | null }) => {
      const rels = c.relationship ?? [];
      allRelationships.push(...rels.filter((r): r is string => !!r));
    });
    const result = uniqueSorted(
      allRelationships.filter((t) => t.toLowerCase() !== 'other')
    );

    return result;
  }, [allContactsData]);

  // Server config snapshot (types/orderMode preserved; we do not edit them for contacts)
  const serverTypes: string[] = useMemo(
    () => cfgData?.contactTypeConfig?.types ?? [],
    [cfgData]
  );
  const serverOrderMode: 'ALPHA' | 'MANUAL' =
    (cfgData?.contactTypeConfig?.orderMode as 'ALPHA' | 'MANUAL') ?? 'ALPHA';
  const serverNavTypes: string[] = useMemo(
    () => cfgData?.contactTypeConfig?.navTypes ?? [],
    [cfgData]
  );
  const serverShowAll: boolean =
    cfgData?.contactTypeConfig?.showAllInNav ?? true;
  // isCustomized is read on demand from cfgData when needed

  const [navTypes, setNavTypes] = useState<string[]>(serverNavTypes);
  const isCustomizedServer =
    (cfgData?.contactTypeConfig?.isCustomized as boolean | undefined) === true;
  const navTypesInitializedRef = useRef(false);
  const inconsistentStateFixedRef = useRef(false);
  useEffect(() => {
    if (navTypesInitializedRef.current) return;
    // Initialize the picker with any saved server selection when available,
    // even if the backend didn't flag it as customized. This keeps the
    // dropdown in sync with the actual Contacts sub-navigation.
    if (serverNavTypes.length > 0) setNavTypes(serverNavTypes);
    navTypesInitializedRef.current = true;
  }, [serverNavTypes, isCustomizedServer]);

  const [showAllNav, setShowAllNav] = useState<boolean>(serverShowAll);
  useEffect(() => {
    if (typeof serverShowAll === 'boolean') setShowAllNav(serverShowAll);
  }, [serverShowAll]);
  useEffect(() => {
    setSortMode(serverOrderMode === 'MANUAL' ? 'manual' : 'alpha');
  }, [serverOrderMode]);

  const [, updateConfig] = useMutation(UPDATE_CONTACT_TYPE_CONFIG_M);
  const [, updateContact] = useMutation(UPDATE_CONTACT_M);

  // Background updater for contacts (supports array relationship field)
  const bg = useBackgroundTypeUpdater<
    {
      id: string;
      name: string;
      status: string;
      type: ContactType;
      relationship?: string[] | null;
    },
    ContactInput
  >({
    getType: (c) => c.relationship ?? [],
    getName: (c) => c.name,
  });

  // Mapping state
  const relPreset = useMemo(() => presetData?.contactTypes ?? [], [presetData]);
  // Use types derived from actual contacts (more reliable than server-provided list)
  const relInUse = useMemo(() => {
    // Combine server-provided list with types computed from actual contact data
    const serverProvided = (inUseData?.contactTypesInUse ?? []).filter(
      (v: string) => !!v
    );

    const result = uniqueSorted([...serverProvided, ...typesInUseFromContacts]);

    return result;
  }, [inUseData, typesInUseFromContacts]);
  const knownTypes = useMemo(
    () =>
      uniqueSorted(
        [...(relPreset || []), ...(relInUse || [])].filter(
          (t) => t.toLowerCase() !== 'other'
        )
      ),
    [relPreset, relInUse]
  );

  // Tooltip state
  const [showHelp, setShowHelp] = useState(false);
  const helpAnchorRef = useRef<HTMLButtonElement | null>(null);

  const [busy, setBusy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const lastResolvedDeletionRef = useRef<string | null>(null);

  // Editor: text + sort mode and options (placed before persistConfig to satisfy dependencies)
  const baseList = useMemo(() => {
    if (serverTypes.length) {
      const extrasInUse = knownTypes.filter(
        (t) => !serverTypes.includes(t) && t.toLowerCase() !== 'other'
      );
      return [...serverTypes, ...uniqueSorted(extrasInUse)];
    }
    return knownTypes;
  }, [serverTypes, knownTypes]);
  const [text, setText] = useState<string>(baseList.join('\n'));
  const [sortMode, setSortMode] = useState<'alpha' | 'manual'>(
    serverOrderMode === 'MANUAL' ? 'manual' : 'alpha'
  );
  const getTypesFromText = useCallback(
    () => deriveTypesFromText(text, sortMode),
    [text, sortMode]
  );

  // Safeguard dialog state (multi-delete) – after text/sortMode are defined
  const multi = useMultiTypeReassignState({
    text,
    sortMode,
    setText: (next) => setText(next),
    getTypesFromText: () => deriveTypesFromText(text, sortMode),
  });
  const {
    deletedTypes,
    setDeletedTypes,
    reassignMap,
    setReassignMap,
    newTypeNames,
    setNewTypeNames,
    availableReassignOptions,
  } = multi;

  // Keep textarea in sync with latest server data on first load or when
  // user hasn't started editing yet. Avoid clobbering user edits.
  const prevBaseListRef = useRef<string[]>(baseList);
  useEffect(() => {
    const prevBase = prevBaseListRef.current.join('\n');
    if (text.trim().length === 0 || text === prevBase) {
      setText(baseList.join('\n'));
    }
    prevBaseListRef.current = baseList;
  }, [baseList, text]);

  // Guard against concurrent persist operations
  const isPersistingRef = useRef(false);

  // serialize config mutations to avoid race conditions and out-of-order state
  const persistConfig = useCallback(
    async (params: {
      types?: string[];
      navTypes?: string[];
      orderMode?: 'ALPHA' | 'MANUAL';
      showAllInNav?: boolean;
      isCustomized?: boolean;
    }) => {
      const run = async () => {
        try {
          isPersistingRef.current = true;
          const currentTypes =
            params.types ?? text.split('\n').map((l) => l.trim());
          const typesToPersist = Array.from(
            new Set(
              (sortMode === 'manual'
                ? currentTypes
                : uniqueSorted(currentTypes)
              ).filter((t) => !!t && t.toLowerCase() !== 'other')
            )
          );
          const baseNav = params.navTypes ?? navTypes;
          const filteredNavTypes = baseNav.filter((t) =>
            typesToPersist.includes(t)
          );
          const navTypesToUse = params.navTypes
            ? params.navTypes
            : filteredNavTypes;
          const orderMode =
            params.orderMode ?? (sortMode === 'manual' ? 'MANUAL' : 'ALPHA');
          const showAll = params.showAllInNav ?? showAllNav;
          const isCustomized = params.isCustomized ?? navTypesToUse.length > 0;
          await updateConfig({
            input: {
              types: typesToPersist,
              orderMode,
              navTypes: navTypesToUse,
              showAllInNav: showAll,
              isCustomized,
            },
          });
        } finally {
          isPersistingRef.current = false;
        }
      };
      return await serializeByKey('contact-types', run);
    },
    [text, sortMode, navTypes, showAllNav, updateConfig]
  );

  // Editor: derived options

  const [recentlyRenamedFromLocal, setRecentlyRenamedFromLocal] = useState<
    string | null
  >(null);

  // const getTypesFromText = useCallback(() => navOptions, [navOptions]);

  const handleToggleShowAllNav = useCallback(
    async (next: boolean) => {
      const prev = showAllNav;
      setShowAllNav(next);
      try {
        writeShowAllToStorage('contacts', tenantKey, next, localStorage);
      } catch {
        // ignore
      }
      const isCustomizedValue =
        cfgData?.contactTypeConfig?.isCustomized ?? serverNavTypes.length > 0;
      if (!cfgData?.contactTypeConfig) return;
      try {
        await persistConfig({
          types: serverTypes,
          orderMode: serverOrderMode,
          navTypes: serverNavTypes,
          showAllInNav: next,
          isCustomized: isCustomizedValue,
        });
      } catch (err) {
        setShowAllNav(prev);
        try {
          writeShowAllToStorage('contacts', tenantKey, prev, localStorage);
        } catch {
          /* ignore rollback storage errors */
        }
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to update navigation setting.';
        setError(message);
      }
    },
    [
      showAllNav,
      tenantKey,
      localStorage,
      cfgData?.contactTypeConfig,
      serverTypes,
      serverOrderMode,
      serverNavTypes,
      persistConfig,
    ]
  );

  // no labelFor mapping; raw chip values are shown

  // Stable handler so sortable doesn't get re-initialized mid-drag
  const handleNavTypesChange = useCallback(
    (next: string[]) => {
      const hasOther = next.includes('Other');
      const withoutOther = next.filter((t) => t !== 'Other');
      const normalized = hasOther ? [...withoutOther, 'Other'] : withoutOther;
      const deduped = Array.from(
        normalized.reduce((acc, cur) => {
          if (!acc.has(cur)) acc.add(cur);
          return acc;
        }, new Set<string>())
      );
      setNavTypes(deduped);
    },
    [setNavTypes]
  );

  // Textarea should show raw user-entered keys (not translated labels)
  // No additional mapping is needed; we rely on NavTypesPicker to translate chips.

  // Memoized callback to check if a type is in use (cached data)
  // Prevents unnecessary re-executions of useTypesEditingFlow effect
  const getInUseCached = useCallback(
    (type: string) => {
      const lc = (v: string) => v.trim().toLowerCase();
      const list = inUseData?.contactTypesInUse ?? [];
      return list.some((t: string) => lc(t) === lc(type));
    },
    [inUseData?.contactTypesInUse]
  );

  // Memoized callback to check if a type is in use (live check)
  // Prevents unnecessary re-executions of useTypesEditingFlow effect
  const liveCheckInUse = useCallback(
    async (type: string, signal?: AbortSignal) => {
      const lc = (v: string) => v.trim().toLowerCase();
      const res = await client
        .query(CONTACTS_RELLESS_Q, {}, networkOnlyWithSignal(signal))
        .toPromise();
      const list: string[] = res.data?.contactTypesInUse ?? [];
      return list.some((t) => lc(t) === lc(type));
    },
    [client]
  );

  const rememberScroll = () => {
    if (textAreaRef.current) scrollPos.current = textAreaRef.current.scrollTop;
  };

  // Memoized callback for prompting multi-reassign
  // Prevents unnecessary re-executions of useTypesEditingFlow effect
  const onPromptReassignMulti = useCallback(
    (items: Array<{ type: string; originalPosition: number }>) => {
      rememberScroll();
      setDeletedTypes(items);
    },
    [setDeletedTypes]
  );

  // Shared editing flow: debounce + rename detection + nav sync + deletion candidates
  const { recentlyRenamedFrom: recentlyRenamedFromHook } = useTypesEditingFlow({
    text,
    sortMode,
    baseList,
    setNavTypes,
    getInUseCached,
    liveCheckInUse,
    isPersisting: isPersistingRef.current,
    deletedDialogOpen: deletedTypes.length > 0,
    liveDeletionPrompts: true,
    onPromptReassignMulti,
  });
  const recentlyRenamedFrom =
    recentlyRenamedFromHook ?? recentlyRenamedFromLocal;

  useEffect(() => {
    // Track recentlyRenamedFrom changes
  }, [recentlyRenamedFromHook, recentlyRenamedFromLocal, recentlyRenamedFrom]);

  const navOptions = useMemo(() => {
    const currentTypes = uniqueSorted(getTypesFromText());
    const result = currentTypes
      .filter((t) => t.toLowerCase() !== 'other')
      .filter((t) =>
        recentlyRenamedFrom
          ? t.toLowerCase() !== recentlyRenamedFrom.toLowerCase()
          : true
      );
    return result;
  }, [getTypesFromText, recentlyRenamedFrom]);

  // Build display value: use customization if present; otherwise default to types in use
  const inferredNavTypes = useMemo(() => {
    // Default to showing types that are actually in use (not just all available types)
    const typesToShow = relInUse.filter(
      (t: string) => navOptions.includes(t) && t.toLowerCase() !== 'other'
    );

    if (typesToShow.length === 0) {
      // Fallback to alphabetical if no types in use
      const allAlpha = [...navOptions].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
      const result = allAlpha.slice(
        0,
        Math.min(MAX_INFERRED_NAV_ITEMS, allAlpha.length)
      );

      return result;
    }

    // Sort alphabetically and limit to max items
    const sorted = [...typesToShow].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    const result = sorted.slice(
      0,
      Math.min(MAX_INFERRED_NAV_ITEMS, sorted.length)
    );

    return result;
  }, [navOptions, relInUse]);

  // Prefer explicit selections whenever present; otherwise fall back to inferred defaults
  const hasCustomNavSelection = navTypes.length > 0;
  const displayNavTypes = hasCustomNavSelection ? navTypes : inferredNavTypes;

  useEffect(() => {
    // Track navTypes changes
  }, [
    navTypes,
    inferredNavTypes,
    displayNavTypes,
    recentlyRenamedFrom,
    recentlyRenamedFromHook,
    recentlyRenamedFromLocal,
  ]);

  // Auto-fix inconsistent server state: isCustomized=true but navTypes is empty
  // This can happen if the server config gets corrupted or after certain migrations
  useEffect(() => {
    if (inconsistentStateFixedRef.current) return;
    if (!navTypesInitializedRef.current) return;

    // Detect inconsistent state: server says customized but has no navTypes
    const hasInconsistentState =
      isCustomizedServer && serverNavTypes.length === 0;

    if (hasInconsistentState && inferredNavTypes.length > 0) {
      inconsistentStateFixedRef.current = true;

      // Fix by persisting the inferred types with isCustomized flag set correctly
      void persistConfig({
        types: serverTypes.length > 0 ? serverTypes : inferredNavTypes,
        orderMode: serverOrderMode,
        navTypes: inferredNavTypes,
        showAllInNav: serverShowAll,
        isCustomized: true,
      })
        .then(() => {
          // Update local state to match
          setNavTypes(inferredNavTypes);
        })
        .catch((err) => {
          console.error(
            '[ManageContactTypes] Failed to fix inconsistent state:',
            err
          );
          inconsistentStateFixedRef.current = false;
        });
    }
  }, [
    isCustomizedServer,
    serverNavTypes,
    inferredNavTypes,
    serverTypes,
    serverOrderMode,
    serverShowAll,
    persistConfig,
  ]);

  // Count contacts for a deleted relationship when dialog is open
  // Info counts computed on-demand

  // Refresh in-use/preset caches when a contact type is renamed so reopen uses fresh data
  useEffect(() => {
    const onRenamed = async () => {
      try {
        await client
          .query(CONTACT_TYPES_IN_USE_Q, undefined, networkOnly)
          .toPromise();
        await client
          .query(CONTACT_TYPES_PRESET_Q, { preset: true }, networkOnly)
          .toPromise();
      } catch {
        // ignore
      }
    };
    window.addEventListener('contactTypeRenamed', onRenamed as EventListener);
    return () =>
      window.removeEventListener(
        'contactTypeRenamed',
        onRenamed as EventListener
      );
  }, [client]);

  const isDirty = useMemo(() => {
    const currentTypes = deriveTypesFromText(text, sortMode);
    const orderModeNow: 'ALPHA' | 'MANUAL' =
      sortMode === 'manual' ? 'MANUAL' : 'ALPHA';
    if (!arraysEqual(currentTypes, serverTypes)) return true;
    if (orderModeNow !== serverOrderMode) return true;
    const serverNavFiltered = serverNavTypes.filter((t) =>
      currentTypes.includes(t)
    );
    if (!arraysEqual(navTypes, serverNavFiltered)) return true;
    if (showAllNav !== serverShowAll) return true;
    return false;
  }, [
    text,
    sortMode,
    navTypes,
    showAllNav,
    serverTypes,
    serverOrderMode,
    serverNavTypes,
    serverShowAll,
  ]);
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // options provided by multi state
  const restoreScroll = () => {
    requestAnimationFrame(() => {
      if (textAreaRef.current)
        textAreaRef.current.scrollTop = scrollPos.current;
    });
  };

  const closeSafeguard = () => {
    // Restore any deleted types back to the textarea in their original positions
    multi.closeSafeguard();
    restoreScroll();
  };

  const performReassign = useCallback(async () => {
    if (deletedTypes.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const current = deriveTypesFromText(text, sortMode);
      const withoutDeleted = current.filter(
        (t) => !deletedTypes.some((d) => d.type === t)
      );

      // Prepare tasks and next preset so we can persist and close immediately
      const tasks = deletedTypes.map((deletion) => {
        let target = reassignMap[deletion.type] || '';
        if (target === '__create__')
          target = (newTypeNames[deletion.type] || '').trim();
        if (!target) target = OTHER_TYPE;
        return { from: deletion.type, to: target };
      });
      const newTypesToAdd = new Set<string>(
        tasks
          .map((t) => t.to)
          .filter((t) => t.toLowerCase() !== OTHER_TYPE.toLowerCase())
      );

      const nextPreset = uniqueSorted([
        ...withoutDeleted,
        ...Array.from(newTypesToAdd),
      ]);
      const filteredNavTypes = navTypes.filter((t) => nextPreset.includes(t));
      await persistConfig({
        types: nextPreset,
        orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
        navTypes: filteredNavTypes,
        showAllInNav: showAllNav,
        isCustomized: filteredNavTypes.length > 0,
      });

      // Update local list and close immediately
      setText(nextPreset.join('\n'));
      setDeletedTypes([]);
      setReassignMap({});
      setNewTypeNames({});
      onDirtyChange?.(false);

      // Dispatch event to notify navigation that types were deleted
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('contactTypeDeleted', {
              detail: { deletedTypes: deletedTypes.map((d) => d.type) },
            })
          );
        }
      } catch {
        /* ignore */
      }

      onClose();

      // Background bulk reassign updates using shared hook
      void (async () => {
        try {
          // Fetch fresh contact data for accurate count
          const fresh = await client
            .query(
              gql`
                query AllContactsFresh_Bulk {
                  contacts(status: [Active, Inactive]) {
                    id
                    name
                    status
                    type
                    relationship
                  }
                }
              `,
              {},
              networkOnly
            )
            .toPromise();
          const freshContacts: Array<{
            id: string;
            name: string;
            status: string;
            type: ContactType;
            relationship?: string[] | null;
          }> = fresh.data?.contacts ?? [];

          progress.start({
            label: 'Updating contacts…',
            delayMs: 300,
            persistent: true,
          });

          await bg.runBulkReassignUpdates({
            tasks,
            allEntities: freshContacts,
            buildInput: (c, from, dest): ContactInput => {
              const rel = (c.relationship ?? []).filter(
                (v): v is string => !!v
              );
              const lc = (v: string) => v.trim().toLowerCase();
              const withoutFrom = rel.filter((r) => lc(r) !== lc(from));
              const withoutDupes = withoutFrom.filter(
                (r) => lc(r) !== lc(dest)
              );
              const next =
                dest === OTHER_TYPE ? withoutDupes : [...withoutDupes, dest];
              // Dedupe case-insensitively, prefer last occurrence (ensures dest casing wins)
              const deduped = Array.from(
                new Map(next.map((v) => [lc(v), v])).values()
              );
              return {
                name: c.name,
                status: c.status as Status,
                type: c.type,
                relationship: uniqueSorted(deduped),
              };
            },
            updateEntity: (vars, ctx) => updateContact(vars, ctx),
            progress,
            opId: `contact-bulk-${Date.now()}`,
            retries: 2,
            managedProgress: true,
          });

          // Refresh caches after all reassignments complete
          try {
            await client
              .query(
                CONTACTS_REFRESH_Q,
                { status: ['Active', 'Inactive'] },
                networkOnly
              )
              .toPromise();
            await client
              .query(CONTACT_TYPE_MAPPINGS_Q, undefined, networkOnly)
              .toPromise();
          } catch {
            // ignore refresh errors; background cache update only
          }

          // Auto-dismiss the progress toast on success
          progress.done('Contacts updated');
        } catch (e) {
          const message =
            e instanceof Error ? e.message : 'Failed to update contacts';
          try {
            progress.error(message);
          } catch {
            /* ignore */
          }
          try {
            toast.error(message);
          } catch {
            /* ignore */
          }
        }
      })();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : String(e ?? '');
      setError(message || 'Failed to reassign contacts.');
      progress.error('Failed to update contacts');
    } finally {
      setBusy(false);
    }
  }, [
    deletedTypes,
    reassignMap,
    newTypeNames,
    text,
    sortMode,
    navTypes,
    persistConfig,
    showAllNav,
    updateContact,
    client,
    setDeletedTypes,
    setReassignMap,
    setNewTypeNames,
    onDirtyChange,
    progress,
    onClose,
    bg,
  ]);

  // Save and persist types/order/nav with simple rename cascade
  const handleSave = useCallback(async () => {
    // Guard against concurrent saves
    if (isPersistingRef.current) {
      setError('A save is already in progress. Please wait.');
      setIsSaving(false);
      return;
    }

    setIsSaving(true);
    const newTypes = deriveTypesFromText(text, sortMode);
    const removedFromBase = (baseList || []).filter(
      (t) => !newTypes.includes(t)
    );
    const addedToBase = newTypes.filter((t) => !(baseList || []).includes(t));

    // Compute displayNavTypes without recentlyRenamedFrom filter for save operation
    // This ensures we preserve types that were visible before the rename
    const currentTypesForNav = uniqueSorted(getTypesFromText()).filter(
      (t) => t.toLowerCase() !== 'other'
    );
    const navOptionsForSave = currentTypesForNav; // No recentlyRenamedFrom filter

    // Include removedFromBase types (the 'from' types in renames) so they can be preserved
    // This handles the case where a type was in inferredNavTypes but is no longer in relInUse
    // We include removedFromBase types if they're in baseList (original types)
    const typesToShowForSave = [
      ...relInUse.filter(
        (t: string) =>
          navOptionsForSave.includes(t) && t.toLowerCase() !== 'other'
      ),
      ...removedFromBase.filter(
        (t) => (baseList || []).includes(t) && t.toLowerCase() !== 'other'
      ),
    ];
    const uniqueTypesToShow = Array.from(new Set(typesToShowForSave));

    const inferredNavTypesForSave =
      uniqueTypesToShow.length === 0
        ? [...navOptionsForSave]
            .sort((a, b) =>
              a.localeCompare(b, undefined, { sensitivity: 'base' })
            )
            .slice(
              0,
              Math.min(MAX_INFERRED_NAV_ITEMS, navOptionsForSave.length)
            )
        : [...uniqueTypesToShow]
            .sort((a, b) =>
              a.localeCompare(b, undefined, { sensitivity: 'base' })
            )
            .slice(
              0,
              Math.min(MAX_INFERRED_NAV_ITEMS, uniqueTypesToShow.length)
            );
    const displayNavTypesForSave =
      navTypes.length > 0 ? navTypes : inferredNavTypesForSave;

    // Multi-rename path (DRY): skip reassign and persist once
    // Use displayNavTypesForSave instead of displayNavTypes to preserve types before recentlyRenamedFrom filter
    const saveMulti = await saveMultiRenamesIfAny({
      baseList,
      newTypes,
      sortMode,
      navTypes: displayNavTypesForSave,
      showAllNav,
      persistConfig: async (args) => {
        await persistConfig({
          types: args.types,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: args.navTypes,
          showAllInNav: showAllNav,
          isCustomized: (args.navTypes ?? []).length > 0,
        });
        // Refetch cache after persist (non-blocking)
        void client.query(CONTACT_TYPE_CONFIG_Q, {}, networkOnly).toPromise();
        void client.query(CONTACT_TYPES_IN_USE_Q, {}, networkOnly).toPromise();
        void client
          .query(CONTACT_TYPES_PRESET_Q, { preset: true }, networkOnly)
          .toPromise();
        void client
          .query(
            gql`
              query ContactsRelless_Nav {
                hasUntypedContacts
                contactTypesInUse
              }
            `,
            {},
            networkOnly
          )
          .toPromise();
      },
      broadcast: (from, to) => {
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('contactTypeRenamed', { detail: { from, to } })
            );
          }
        } catch {
          /* ignore */
        }
      },
    });
    if (saveMulti.handled) {
      setIsSaving(true);
      try {
        setRecentlyRenamedFromLocal(
          saveMulti.renamePairs[saveMulti.renamePairs.length - 1].from
        );
        setText(saveMulti.newTypesNormalized.join('\n'));
        setNavTypes(saveMulti.navTypesNext);

        // Brief delay to present button loading
        await new Promise((resolve) => setTimeout(resolve, 300));

        onDirtyChange?.(false);
        onClose();

        // Background updates for all rename pairs using shared hook
        void (async () => {
          try {
            // Fetch fresh contact data for accurate count
            const fresh = await client
              .query(
                gql`
                  query AllContactsFresh {
                    contacts(status: [Active, Inactive]) {
                      id
                      name
                      status
                      type
                      relationship
                    }
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();
            const freshContacts: Array<{
              id: string;
              name: string;
              status: string;
              type: ContactType;
              relationship?: string[] | null;
            }> = fresh.data?.contacts ?? [];

            // Use bulk reassign to apply all renames in one pass per contact
            // This prevents sequential updates from overwriting each other
            await bg.runBulkReassignUpdates({
              tasks: saveMulti.renamePairs,
              allEntities: freshContacts,
              buildInput: (c): ContactInput => {
                const rel = (c.relationship ?? []).filter(
                  (v): v is string => !!v
                );
                const lc = (v: string) => v.trim().toLowerCase();
                // Apply ALL pending renames, not just the current one
                const transformed = rel.map((r) => {
                  const pair = saveMulti.renamePairs.find(
                    (p) => lc(p.from) === lc(r)
                  );
                  return pair ? pair.to : r;
                });
                // Dedupe case-insensitively, preserving last occurrence
                const deduped = Array.from(
                  new Map(transformed.map((v) => [lc(v), v])).values()
                );
                return {
                  name: c.name,
                  status: c.status as Status,
                  type: c.type,
                  relationship: uniqueSorted(deduped),
                };
              },
              updateEntity: (vars, ctx) => updateContact(vars, ctx),
              progress,
              opId: `contact-multi-rename-${Date.now()}`,
              retries: 2,
              managedProgress: true,
            });

            // Refresh caches after all renames complete
            try {
              await client
                .query(
                  CONTACTS_REFRESH_Q,
                  { status: ['Active', 'Inactive'] },
                  networkOnly
                )
                .toPromise();
              await client
                .query(CONTACT_TYPE_MAPPINGS_Q, undefined, networkOnly)
                .toPromise();
            } catch {
              // ignore refresh errors; background cache update only
            }

            // When all pairs complete successfully, close out the single toast
            progress.done('Contacts updated');
          } catch (e) {
            const message =
              e instanceof Error
                ? e.message
                : 'Failed to update contacts after rename.';
            try {
              progress.error(message);
            } catch {
              /* ignore */
            }
            try {
              toast.error(message);
            } catch {
              /* ignore */
            }
          }
        })();

        return;
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e ?? '');
        setError(message || 'Failed to rename relationships.');
        return;
      } finally {
        setIsSaving(false);
      }
    }

    if (removedFromBase.length === 1 && addedToBase.length === 1) {
      // Handle rename: use server-side mutation for instant rename
      const from = removedFromBase[0];
      const to = addedToBase[0];
      try {
        setRecentlyRenamedFromLocal(from);
        setText((prev) => {
          const lines = prev.split('\n');
          let replaced = false;
          const next = lines
            .map((line) => {
              const value = line.trim();
              if (value === from) {
                replaced = true;
                return to;
              }
              return line;
            })
            .filter(Boolean) as string[];
          if (!replaced) next.push(to);
          return deriveTypesFromText(next.join('\n'), sortMode).join('\n');
        });
        // Update nav types selection to reflect rename and filter to current types
        // Reflect rename in selected chips, filter to current types, and dedupe
        // Use displayNavTypes instead of navTypes to preserve inferred nav types when renaming
        const navTypesNext = Array.from(
          new Set(
            displayNavTypes
              .map((t) => (t === from ? to : t))
              .filter((t) => newTypes.includes(t))
          )
        );
        setNavTypes(navTypesNext);

        // Persist config FIRST to update server and cache immediately
        await persistConfig({
          types: newTypes,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: navTypesNext,
          showAllInNav: showAllNav,
          isCustomized: navTypesNext.length > 0,
        });

        // Force refetch of contact types for instant UI update (non-blocking)
        void client.query(CONTACT_TYPE_CONFIG_Q, {}, networkOnly).toPromise();
        void client.query(CONTACT_TYPES_IN_USE_Q, {}, networkOnly).toPromise();
        void client
          .query(CONTACT_TYPES_PRESET_Q, { preset: true }, networkOnly)
          .toPromise();
        void client
          .query(
            gql`
              query ContactsRelless_Nav {
                hasUntypedContacts
                contactTypesInUse
              }
            `,
            {},
            networkOnly
          )
          .toPromise();

        // Dispatch event immediately so UI updates
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('contactTypeRenamed', { detail: { from, to } })
            );
          }
        } catch {
          // ignore event dispatch failures
        }

        // Trigger background updates and close immediately
        void (async () => {
          try {
            // Fetch fresh contact data for accurate count
            const fresh = await client
              .query(
                gql`
                  query AllContactsFresh_Single {
                    contacts(status: [Active, Inactive]) {
                      id
                      name
                      status
                      type
                      relationship
                    }
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();
            const freshContacts: Array<{
              id: string;
              name: string;
              status: string;
              type: ContactType;
              relationship?: string[] | null;
            }> = fresh.data?.contacts ?? [];

            const normalize = (v: unknown) =>
              (v == null ? '' : String(v)).trim().toLowerCase();
            const totalToUpdate = freshContacts.filter((c) =>
              (c.relationship ?? []).some(
                (rel) => normalize(rel) === normalize(from)
              )
            ).length;
            progress.start({
              label: 'Updating contacts…',
              delayMs: 0,
              persistent: true,
            });
            if (totalToUpdate > 0) progress.setTotal(totalToUpdate);

            await bg.runRenameUpdates({
              from,
              to,
              allEntities: freshContacts,
              buildInput: (c, from, dest): ContactInput => {
                const rel = (c.relationship ?? []).filter(
                  (v): v is string => !!v
                );
                const lc = (v: string) => v.trim().toLowerCase();
                const withoutFrom = rel.filter((r) => lc(r) !== lc(from));
                const withoutDupes = withoutFrom.filter(
                  (r) => lc(r) !== lc(dest)
                );
                const next = [...withoutDupes, dest];
                const deduped = Array.from(
                  new Map(next.map((v) => [lc(v), v])).values()
                );
                return {
                  name: c.name,
                  status: c.status as Status,
                  type: c.type,
                  relationship: uniqueSorted(deduped),
                };
              },
              updateEntity: (vars, ctx) => updateContact(vars, ctx),
              progress,
              opId: `contact-rename-${from}-${to}-${Date.now()}`,
              retries: 2,
              managedProgress: true,
            });

            // Refresh contacts list after update completes
            try {
              await client
                .query(
                  CONTACTS_REFRESH_Q,
                  { status: ['Active', 'Inactive'] },
                  networkOnly
                )
                .toPromise();
              await client
                .query(CONTACT_TYPE_MAPPINGS_Q, undefined, networkOnly)
                .toPromise();
            } catch {
              // ignore refresh failures
            }

            progress.done('Contacts updated');
          } catch (e) {
            const message =
              e instanceof Error
                ? e.message
                : 'Failed to update contacts after rename.';
            try {
              progress.error(message);
            } catch {
              /* ignore */
            }
            try {
              toast.error(message);
            } catch {
              /* ignore */
            }
          }
        })();

        // Clear dirty state and close promptly while updates continue
        onDirtyChange?.(false);
        onClose();

        return;
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e ?? '');
        setError(message || 'Failed to rename relationship.');
        try {
          toast.error('Failed to rename contact type.');
        } catch {
          // Ignore toast errors
        }
        return;
      } finally {
        setRecentlyRenamedFromLocal(null);
        setIsSaving(false);
      }
    }

    // If deletions include in-use types, open safeguard dialog for the first one
    if (removedFromBase.length > 0) {
      // If we've just resolved a deletion via reassignment, skip re-prompting once
      const afterSkip = removedFromBase.filter(
        (t) => t !== lastResolvedDeletionRef.current
      );
      if (afterSkip.length === 0) {
        lastResolvedDeletionRef.current = null;
      }
      // Live-check ALL removed types and prompt with complete list like assets
      const deletions: Array<{ type: string; originalPosition: number }> = [];
      for (const t of afterSkip) {
        try {
          const res = await client
            .query<{ contacts: Array<{ id: string }> }>(
              CONTACTS_BY_REL_Q,
              { relationship: [t] },
              networkOnly
            )
            .toPromise();
          const count = (res.data?.contacts ?? []).length;
          if (count > 0) {
            deletions.push({
              type: t,
              originalPosition: (baseList || []).indexOf(t),
            });
          }
        } catch {
          // ignore
        }
      }
      if (deletions.length > 0) {
        setError('');
        setInfo('');
        rememberScroll();
        setDeletedTypes(deletions);
        return;
      }
    }

    const filteredNavTypes = navTypes.filter((t) => newTypes.includes(t));
    await persistConfig({
      types: newTypes,
      orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
      navTypes: filteredNavTypes,
      showAllInNav: showAllNav,
      isCustomized: filteredNavTypes.length > 0,
    });
    // Await cache refresh so nav reflects latest config before closing
    await Promise.all([
      client.query(CONTACT_TYPE_CONFIG_Q, {}, networkOnly).toPromise(),
      client.query(CONTACT_TYPES_IN_USE_Q, {}, networkOnly).toPromise(),
      client
        .query(
          gql`
            query ContactsRelless_Nav {
              hasUntypedContacts
              contactTypesInUse
            }
          `,
          {},
          networkOnly
        )
        .toPromise(),
    ]);

    // Dispatch event if types were removed without reassignment (simple deletions)
    if (removedFromBase.length > 0 && addedToBase.length === 0) {
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('contactTypeDeleted', {
              detail: { deletedTypes: removedFromBase },
            })
          );
        }
      } catch {
        /* ignore */
      }
    }

    // Clear dirty state before closing
    onDirtyChange?.(false);
    setIsSaving(false);
    onClose();
  }, [
    baseList,
    navTypes,
    onClose,
    persistConfig,
    showAllNav,
    sortMode,
    client,
    updateContact,
    onDirtyChange,
    setDeletedTypes,
    progress,
    bg,
    text,
  ]);

  // Expose save handler to parent when external actions are used
  // Avoid passing a changing function reference to parent on every render.
  // We update a ref when handleSave changes, and provide a stable wrapper once.
  const saveRef = useRef<() => void>(() => {});
  useEffect(() => {
    saveRef.current = handleSave;
  }, [handleSave]);
  useEffect(() => {
    if (onSaveReady) onSaveReady(() => saveRef.current());
  }, [onSaveReady]);

  return (
    <div
      className='flex flex-col gap-3'
      onClick={(e) => {
        // Prevent clicks within the form from bubbling to parent backdrop
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        // Prevent keyboard events (especially backspace) from bubbling to parent dialog
        e.stopPropagation();
      }}
    >
      <p className='mb-4 ml-1 text-sm text-secondary'>
        Customize which Contact categories appear as tabs in the Contacts
        sub-navigation. Unselected categories with contacts will be grouped
        under “Other”.
      </p>

      {/* Nav selection */}
      <div className='mb-1 flex flex-col gap-1'>
        <div className='flex items-center justify-between'>
          <Label as='span'>Contacts Sub-Navigation</Label>
          <div className='flex items-center gap-2'>
            <div
              className='relative inline-flex items-center'
              onMouseEnter={() => setShowHelp(true)}
            >
              <button
                type='button'
                aria-label='Contacts Sub-Navigation help'
                className='ml-1 inline-flex h-5 w-5 items-center justify-center text-secondary focus-visible:outline-none'
                onMouseEnter={() => setShowHelp(true)}
                onMouseLeave={() => setShowHelp(false)}
                onFocus={() => setShowHelp(true)}
                ref={helpAnchorRef}
              >
                <FontAwesomeIcon
                  icon={faInfoCircle as IconProp}
                  className='text-secondary'
                />
              </button>
              <Tooltip
                isVisible={showHelp}
                anchorEl={helpAnchorRef.current}
                placement='top'
                withArrow
                className='min-w-[300px] max-w-[420px] px-3 py-2 text-left'
              >
                <span className='block px-1 pt-2 text-base font-medium text-white'>
                  Customize Contacts Navigation Tabs
                </span>
                <span className='mt-1 block px-1 pb-2 text-sm text-white opacity-90'>
                  Unselected categories with contacts will be grouped under
                  “Other”. Drag chips to reorder; the nav will use this order.
                </span>
              </Tooltip>
            </div>
          </div>
        </div>
        <NavTypesPicker
          options={navOptions}
          value={displayNavTypes}
          onChange={handleNavTypesChange}
        />
        <div className='mt-1 pl-1'>
          <Switch
            name='showAllNav'
            label='Display “All” in sub-navigation'
            help={
              'When enabled, an “All” tab appears first in Contacts sub-navigation.'
            }
            checked={showAllNav}
            onChange={handleToggleShowAllNav}
          />
        </div>
      </div>

      <AutoTextArea
        label='Contact Types'
        className='w-full'
        ref={textAreaRef}
        style={{ paddingBottom: 30 }}
        value={text}
        onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
        placeholder={'One type per line'}
        rows={6}
        description='Enter each choice on a new line. We will automatically add "Other" if it is needed.'
      />
      {errorMessage && (
        <div
          role='alert'
          aria-live='assertive'
          className='w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
        >
          {errorMessage}
        </div>
      )}
      {infoMessage && (
        <div
          role='status'
          aria-live='polite'
          className='w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
        >
          {infoMessage}
        </div>
      )}
      <div className='ml-1 flex w-full justify-start gap-2 pt-0.5'>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='radio'
            checked={sortMode === 'alpha'}
            onChange={() => setSortMode('alpha')}
          />
          Alphabetical
        </label>
        <label className='flex items-center gap-2 text-sm'>
          <input
            type='radio'
            checked={sortMode === 'manual'}
            onChange={() => setSortMode('manual')}
          />
          Manual
        </label>
      </div>

      {!useExternalActions && (
        <div className='mt-2 flex justify-end gap-3'>
          <button
            type='button'
            className='rounded-md px-4 py-2 text-sm font-medium text-brand-dark hover:text-brand'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className='rounded-md border border-brand bg-brand px-5 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50'
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {deletedTypes.length > 0 && (
        <Confirm
          show={true}
          title={'Reassign Contacts from deleted types'}
          cancel={'Cancel'}
          confirm={'Reassign & Save'}
          onCancel={() => {
            closeSafeguard();
          }}
          onConfirm={performReassign}
          isLoading={busy}
          isDisabled={deletedTypes.some(
            (d) =>
              reassignMap[d.type] === '__create__' &&
              !(newTypeNames[d.type] || '').trim()
          )}
        >
          <div className='px-2 text-left'>
            <p className='mb-1'>
              Choose a target type for each deleted type. If left blank,
              contacts will be assigned to “Other”.
            </p>
            <div className='mt-2 flex flex-col gap-3'>
              {deletedTypes.map((d) => (
                <div
                  key={d.type}
                  className='rounded-md border border-grey-20 p-3'
                >
                  <div className='flex items-center justify-between'>
                    <div className='text-sm font-medium'>“{d.type}”</div>
                  </div>
                  {availableReassignOptions.length > 0 ? (
                    <>
                      <select
                        className='input mt-3 w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                        value={reassignMap[d.type] || ''}
                        onChange={(e) =>
                          setReassignMap((prev) => ({
                            ...prev,
                            [d.type]: e.target.value,
                          }))
                        }
                      >
                        <option value=''>Select a type</option>
                        {availableReassignOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                        <option value='__create__'>Create New Type…</option>
                      </select>
                      {reassignMap[d.type] === '__create__' && (
                        <>
                          <input
                            className='input mt-2 w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                            placeholder='New type name'
                            value={newTypeNames[d.type] || ''}
                            onChange={(e) =>
                              setNewTypeNames((prev) => ({
                                ...prev,
                                [d.type]: e.target.value,
                              }))
                            }
                            aria-invalid={!(newTypeNames[d.type] || '').trim()}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <p className='mt-2 text-sm text-secondary'>
                      No other types available. These will be assigned to
                      “Other”.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Confirm>
      )}
    </div>
  );
}
